import * as React from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Alert } from '@/components/ui/alert'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { EmptyState } from '@/components/ui/empty-state'
import { Input } from '@/components/ui/input'
import { deleteJSON, getJSON, postJSON } from '@/lib/api'
import { displayDateTime } from '@/lib/dates'

interface Leader {
  id: number
  contact_id: number
  contact_name: string
  created_at: string
}

interface ContactCandidate {
  id: number
  name: string
  email: string | null
  contact_type?: 'Person' | 'Organization'
}

export function ChapterLeadersSection({
  chapterId,
  readOnly,
}: {
  chapterId: number
  readOnly: boolean
}) {
  const client = useQueryClient()
  const [grantOpen, setGrantOpen] = React.useState(false)
  const [revokeTarget, setRevokeTarget] = React.useState<Leader | null>(null)
  const [search, setSearch] = React.useState('')
  const leadersQuery = useQuery({
    queryKey: ['chapter-leaders', chapterId],
    queryFn: () =>
      getJSON<{ chapter_leaders: Leader[] }>(`/api/v1/chapters/${chapterId}/leadership_grants`),
    retry: false,
  })
  const candidatesQuery = useQuery({
    queryKey: ['chapter-leader-candidates', search],
    queryFn: () =>
      getJSON<{ contacts: ContactCandidate[] }>(
        `/api/v1/contacts?contact_type=person&search=${encodeURIComponent(search)}`,
      ),
    enabled: grantOpen,
    retry: false,
  })
  const grant = useMutation({
    mutationFn: (contactId: number) =>
      postJSON(`/api/v1/chapters/${chapterId}/leadership_grants`, {
        leader: { contact_id: contactId },
      }),
    onSuccess: () => {
      client.invalidateQueries({ queryKey: ['chapter-leaders', chapterId] })
      setGrantOpen(false)
      setSearch('')
    },
  })
  const revoke = useMutation({
    mutationFn: (leaderId: number) =>
      deleteJSON(`/api/v1/chapters/${chapterId}/leadership_grants/${leaderId}`),
    onSuccess: () => {
      client.invalidateQueries({ queryKey: ['chapter-leaders', chapterId] })
      setRevokeTarget(null)
    },
  })
  const leaders = leadersQuery.data?.chapter_leaders ?? []

  return (
    <section className="px-4 py-4">
      <div className="flex items-center justify-between gap-3 pb-3">
        <h3 className="text-body font-medium">Leaders · {leaders.length}</h3>
        {!readOnly && (
          <Button variant="outline" size="sm" onClick={() => setGrantOpen(true)}>
            + Grant leadership
          </Button>
        )}
      </div>
      {leadersQuery.isLoading ? (
        <p className="text-body-sm text-muted-foreground">Loading leaders…</p>
      ) : leadersQuery.isError ? (
        <Alert>Unable to load chapter leaders.</Alert>
      ) : leaders.length === 0 ? (
        <EmptyState
          variant="quiet"
          className="items-start px-0 text-left"
          title="No leaders have been granted access to this chapter yet."
        />
      ) : (
        <div className="divide-y divide-border">
          {leaders.map((leader) => (
            <div
              key={leader.id}
              className="flex items-center justify-between gap-3 py-3 first:pt-0 last:pb-0"
            >
              <div>
                <p className="text-body-sm text-foreground">{leader.contact_name}</p>
                <p className="text-caption text-muted-foreground">
                  Granted {displayDateTime(leader.created_at)}
                </p>
              </div>
              {!readOnly && (
                <Button variant="ghost" size="sm" onClick={() => setRevokeTarget(leader)}>
                  Revoke
                </Button>
              )}
            </div>
          ))}
        </div>
      )}
      <GrantLeadershipDialog
        open={grantOpen}
        onOpenChange={setGrantOpen}
        search={search}
        onSearchChange={setSearch}
        contacts={candidatesQuery.data?.contacts ?? []}
        onGrant={(contactId) => grant.mutate(contactId)}
        error={grant.isError ? 'Unable to grant leadership. Try again.' : undefined}
      />
      <RevokeLeadershipDialog
        leader={revokeTarget}
        onOpenChange={(open) => !open && setRevokeTarget(null)}
        onRevoke={(leaderId) => revoke.mutate(leaderId)}
      />
    </section>
  )
}

function GrantLeadershipDialog({
  open,
  onOpenChange,
  search,
  onSearchChange,
  contacts,
  onGrant,
  error,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
  search: string
  onSearchChange: (value: string) => void
  contacts: ContactCandidate[]
  onGrant: (contactId: number) => void
  error?: string
}) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Grant chapter leadership</DialogTitle>
        </DialogHeader>
        <Input
          aria-label="Search people with a portal sign-in"
          placeholder="Search people with a portal sign-in"
          value={search}
          onChange={(event) => onSearchChange(event.target.value)}
        />
        <p className="text-caption text-muted-foreground">
          Only people who can sign in to the member portal can be granted leadership.
        </p>
        {error && <Alert>{error}</Alert>}
        <div className="flex flex-col gap-1">
          {contacts
            .filter((contact) => contact.contact_type !== 'Organization')
            .map((contact) =>
              contact.email ? (
                <Button
                  key={contact.id}
                  variant="outline"
                  className="justify-start"
                  onClick={() => onGrant(contact.id)}
                >
                  {contact.name} · {contact.email}
                </Button>
              ) : (
                <p key={contact.id} className="px-3 py-2 text-body-sm text-muted-foreground">
                  {contact.name} — unavailable: Member Login required
                </p>
              ),
            )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

function RevokeLeadershipDialog({
  leader,
  onOpenChange,
  onRevoke,
}: {
  leader: Leader | null
  onOpenChange: (open: boolean) => void
  onRevoke: (leaderId: number) => void
}) {
  return (
    <Dialog open={leader !== null} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Revoke leadership from {leader?.contact_name}?</DialogTitle>
        </DialogHeader>
        <p className="text-body-sm">
          This person will immediately lose access to this chapter’s leader tools.
        </p>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button variant="destructive" onClick={() => leader && onRevoke(leader.id)}>
            Revoke leadership
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
